// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useEffect, useState, type PropsWithChildren, type ReactNode } from "react"
import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import { CircleAlert } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useI18n } from "@/i18n"

interface OnboardingStepShellProps extends PropsWithChildren {
  eyebrow: string
  title: string
  body?: string
  onContinue: () => void
  canContinue: boolean
  isLastStep: boolean
  saving: boolean
  saveError?: string | null
  continueLabelOverride?: string
  footerExtra?: ReactNode
  /** A full-width line directly above the continue button. */
  footerNote?: ReactNode
  /** Pure single-select screens auto-advance on tap (owner reference video) — the Continue button would be dead weight there. */
  continueHidden?: boolean
}

/**
 * Shared layout for every onboarding step: eyebrow/title/body, the step's
 * own fields as children, an aria-live error region, and the back/continue
 * footer. Mirrors main.html's onboarding modal structure (eyebrow, h3,
 * intro paragraph, fields, footer) but as a full-page route rather than a
 * modal, so the flow can be deep-linked and resumed.
 */
export function OnboardingStepShell({
  eyebrow,
  title,
  body,
  onContinue,
  canContinue,
  isLastStep,
  saving,
  saveError,
  continueLabelOverride,
  footerExtra,
  footerNote,
  continueHidden = false,
  children,
}: OnboardingStepShellProps) {
  const { t } = useI18n()
  const reduce = useReducedMotion()
  // Owner 2026-08-29 ("like websites with the best user experience"): the
  // Continue button is never silently disabled — an invalid attempt gets an
  // animated required-field callout + a button shake, cleared the moment
  // the step becomes valid.
  const [showMissing, setShowMissing] = useState(false)
  const [shakeKey, setShakeKey] = useState(0)
  useEffect(() => {
    if (canContinue) setShowMissing(false)
  }, [canContinue])
  useEffect(() => {
    setShowMissing(false)
  }, [title])

  function handleContinueClick() {
    if (!canContinue) {
      setShowMissing(true)
      setShakeKey(k => k + 1)
      return
    }
    onContinue()
  }

  return (
    <section
      aria-labelledby="onboarding-step-title"
      className="mx-auto flex w-full max-w-2xl flex-col gap-6"
    >
      <div>
        <p className="text-xs font-semibold tracking-wide text-ink-2 uppercase">{eyebrow}</p>
        <h1
          id="onboarding-step-title"
          className="font-display mt-1 text-3xl font-bold tracking-tight text-ink"
        >
          {title}
        </h1>
        {body && <p className="mt-2 text-sm text-ink-2">{body}</p>}
      </div>

      <div className="flex flex-col gap-5">{children}</div>

      {saveError && (
        <p role="alert" aria-live="polite" className="text-sm text-destructive">
          {saveError === "errors.onboarding.structuredIntoleranceRequired"
            ? t("onboarding.step6.structuredIntoleranceRequired")
            : `${t("onboarding.shell.saveErrorTitle")} — ${t("onboarding.shell.saveErrorBody")}`}
        </p>
      )}

      {/* Back moved into the progress header (OnboardingProgress) — the
          footer is Continue-only, and absent entirely on tap-to-advance
          screens. */}
      <AnimatePresence>
        {showMissing && (
          <motion.p
            role="alert"
            aria-live="assertive"
            initial={reduce ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? undefined : { opacity: 0, y: -4 }}
            transition={{ duration: 0.18 }}
            className="flex items-center gap-2 rounded-bb-lg border border-destructive/30 bg-destructive/5 px-3.5 py-2.5 text-sm font-medium text-destructive"
          >
            <CircleAlert className="size-4 shrink-0" aria-hidden="true" />
            {t("onboarding.shell.missingRequired")}
          </motion.p>
        )}
      </AnimatePresence>

      {!continueHidden && footerNote}
      {!continueHidden && (
        <footer className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:items-center sm:justify-end">
          <div className="flex items-center gap-3 sm:justify-end">
            {footerExtra}
            <motion.div
              key={shakeKey}
              animate={reduce || shakeKey === 0 ? undefined : { x: [0, -7, 7, -5, 5, -2, 0] }}
              transition={{ duration: 0.4 }}
              className="w-full sm:w-auto"
            >
              <Button
                type="button"
                className="min-h-12 w-full rounded-bb-full sm:min-w-44"
                onClick={handleContinueClick}
                disabled={saving}
                aria-disabled={!canContinue || saving}
              >
                {saving
                  ? t("onboarding.shell.saving")
                  : (continueLabelOverride ??
                    (isLastStep ? t("onboarding.shell.finish") : t("onboarding.shell.continue")))}
              </Button>
            </motion.div>
          </div>
        </footer>
      )}
      {continueHidden && saving && (
        <p role="status" className="text-center text-sm text-ink-2">
          {t("onboarding.shell.saving")}
        </p>
      )}
    </section>
  )
}
